import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, GraduationCap, FirstAidKit, Crosshair, CaretRight, CheckCircle } from "@phosphor-icons/react";
import { Reveal } from "@/components/Motion";
import RedCrossBadge from "@/components/RedCrossBadge";

const SHIELD = "/img/brand/sep-shield.webp";

const SERVICES = [
  { icon: ShieldCheck, title: "Wear & Carry Permits", desc: "State-certified permit courses across MD, NJ, DC, VA, AZ, FL and Utah.", code: "TR-01", to: "/training" },
  { icon: Crosshair, title: "Armed Security Officer", desc: "Maryland-compliant certification for armed officer licensure.", code: "TR-02", to: "/training/armed-security-officer" },
  { icon: ShieldCheck, title: "D.C. Armed Special Police", desc: "MPD-compliant initial and renewal courses for commissioned officers.", code: "TR-03", to: "/training/dc-armed-special-police" },
  { icon: FirstAidKit, title: "First Aid / CPR / AED", desc: "American Red Cross–certified life-saving skills for every professional.", code: "TR-04", to: "/training/first-aid-cpr-aed", redCross: true },
  { icon: GraduationCap, title: "eLearning Library", desc: "27 self-paced online courses from U.S. LawShield® — permits, law, marksmanship.", code: "TR-05", to: "/elearning" },
];

const PROCESS = [
  { step: "01", title: "Choose", desc: "Browse the catalog and pick the course that matches your goal or state." },
  { step: "02", title: "Enroll", desc: "Secure your seat with instant online checkout. Pick your class date." },
  { step: "03", title: "Attend", desc: "Show up to our Calverton HQ or the assigned range. Fully briefed materials." },
  { step: "04", title: "Certify", desc: "Leave with your certificate and everything needed to file your application." },
];

export function WhoWeAre() {
  return (
    <section className="border-t border-white/10 section-pad relative" data-testid="who-section">
      <div className="container-x grid grid-cols-12 gap-12 items-center">
        <div className="col-span-12 lg:col-span-5 order-2 lg:order-1">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-8 bg-[#FFD300]/5 blur-3xl rounded-full" />
              <img src={SHIELD} alt="SEP Shield" className="relative w-full max-w-md mx-auto" data-testid="shield-image" />
            </div>
          </Reveal>
        </div>
        <div className="col-span-12 lg:col-span-7 order-1 lg:order-2">
          <Reveal>
            <div className="overline mb-4">// 001 — WHO WE ARE</div>
            <h2 className="font-heading text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-8">
              A Maryland-based firm with a <span className="text-[#FFD300]">law-enforcement-grade</span> mindset.
            </h2>
            <p className="text-neutral-300 leading-relaxed text-lg mb-6">
              Secrecy Executive is a state-certified firearms and security training academy — offering permit
              courses across seven states, professional certifications for security officers and protective agents,
              and life-saving skills like CPR and first aid.
            </p>
            <p className="text-neutral-400 leading-relaxed mb-10">
              Our courses are led by <strong className="text-white">S. Williams</strong> — an active law-enforcement officer,
              multi-state licensed firearms instructor, and founder of the academy — alongside <strong className="text-white">Derek Kinard</strong>,
              an active Maryland police officer and 2023 Anne Arundel County Police Academy graduate. Structured,
              disciplined, and grounded in real-world scenarios.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "State-certified, multi-jurisdiction instructors",
                "Real-world, scenario-based training",
                "Professional, structured learning environment",
                "Focus on safety, legality, and accountability",
              ].map((p, i) => (
                <div key={p} className="flex items-start gap-3 text-sm" data-testid={`who-point-${i}`}>
                  <CheckCircle size={18} weight="duotone" className="text-[#FFD300] mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-200">{p}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function WhatWeTeach() {
  return (
    <section className="section-pad" data-testid="services-section">
      <div className="container-x">
        <Reveal>
          <div className="grid grid-cols-12 gap-6 mb-16">
            <div className="col-span-12 md:col-span-7">
              <div className="overline mb-4">// 003 — WHAT WE TEACH</div>
              <h2 className="font-heading text-4xl md:text-6xl tracking-tighter leading-[0.95]">
                Five pillars.<br/>One uncompromising standard.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:self-end">
              <p className="text-neutral-400 leading-relaxed text-lg">
                Every course is built on rigorous state curricula, hands-on scenarios, and the same
                standard of professionalism we expect from graduates when they carry, guard, or protect.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.title} delay={i * 0.06}>
                <Link to={s.to} className="bg-black p-10 group block h-full relative overflow-hidden" data-testid={`service-card-${i}`}>
                  <div className="flex items-start justify-between mb-8">
                    {s.redCross ? (
                      <RedCrossBadge size={48} withText={false} />
                    ) : (
                      <motion.div whileHover={{ rotate: -6, scale: 1.1 }} transition={{ duration: 0.3 }}>
                        <Icon size={42} weight="thin" className="text-[#FFD300]" />
                      </motion.div>
                    )}
                    <div className="overline">{s.code}</div>
                  </div>
                  <h3 className="font-heading text-2xl tracking-tighter mb-3">{s.title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed mb-6">{s.desc}</p>
                  <div className="btn-ghost group-hover:text-[#FFD300]">Learn more <CaretRight size={12} /></div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function HowWeOperate() {
  return (
    <section className="bg-[#050505] border-y border-white/10 section-pad" data-testid="process-section">
      <div className="container-x">
        <Reveal>
          <div className="grid grid-cols-12 gap-8 mb-16">
            <div className="col-span-12 md:col-span-8">
              <div className="overline mb-4">// 004 — HOW WE OPERATE</div>
              <h2 className="font-heading text-4xl md:text-6xl tracking-tighter leading-[0.95]">From inquiry to certification.<br/><span className="text-[#FFD300]">In four moves.</span></h2>
            </div>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10">
          {PROCESS.map((p, i) => (
            <Reveal key={p.step} delay={i * 0.1}>
              <div className="bg-[#050505] p-10 h-full relative" data-testid={`process-${i}`}>
                <div className="font-mono text-xs text-[#FFD300] mb-8 tracking-[0.2em]">// {p.step}</div>
                <h3 className="font-heading text-3xl tracking-tighter mb-4">{p.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{p.desc}</p>
                {i < PROCESS.length - 1 && (
                  <div className="absolute top-12 right-6 text-neutral-700 hidden md:block">
                    <ArrowRight size={20} />
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
